import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Phone, Mail, User, Search, Key, Copy, CheckCircle } from 'lucide-react';
import ParentEditModal from './ParentEditModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ParentAddModal from './ParentAddModal';
import { useAuth } from '@/App';

const ParentManagement = () => {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [allParents, setAllParents] = useState([]); // Store all for filtering
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  
  // Two separate search boxes
  const [parentNameSearch, setParentNameSearch] = useState('');
  const [childNameSearch, setChildNameSearch] = useState('');
  
  // Password reset dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const {user} = useAuth();

  useEffect(() => {
    fetchParents();
  }, []);

  // Fetch from backend when search terms change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (parentNameSearch || childNameSearch) {
        fetchParents();
      } else if (parentNameSearch === '' && childNameSearch === '') {
        fetchParents();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [parentNameSearch, childNameSearch]);

  const fetchParents = async () => {
    setLoading(true);
    try {
      // Backend now returns enriched parent data with children via aggregation
      const response = await apiClient.get('/v1/admin/parents/', {
        params: { 
          hostel_id: user.hostel_id,
          parent_search: parentNameSearch || undefined,
          child_search: childNameSearch || undefined
        }
      });
      
      setAllParents(response.data);
      setParents(response.data);
    } catch (error) {
      toast.error('Failed to load parents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (parent = null) => {
    setEditingParent(parent);
    setShowModal(true);
  };

  const [addParent, setAddParent] = useState(false);

  const handleAddOpenModal = () => {
    setAddParent(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingParent(null);
    fetchParents(); // Refresh after edit
  };

  const handleDelete = async (parentId, childrenCount) => {
    if (childrenCount > 0) {
      toast.error(`Cannot delete parent with ${childrenCount} assigned child(ren). Remove children first.`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this parent?')) return;
    
    try {
      await apiClient.delete(`/v1/admin/parents/${parentId}`);
      toast.success('Parent deleted successfully');
      fetchParents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete parent');
    }
  };

  const handleChildClick = (studentId) => {
    navigate(`/admin/students/${studentId}`);
  };

  const handleResetPassword = async (e, parentId, parentName) => {
    e.stopPropagation();
    
    if (!window.confirm(`Reset password for ${parentName}?\n\nA new random password will be generated.`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/v1/admin/parents/${parentId}/reset-password`);
      
      setResetPasswordData({
        parentName: response.data.parent_name,
        phone: response.data.phone,
        newPassword: response.data.new_password,
        loginInstructions: response.data.login_instructions
      });
      setShowPasswordDialog(true);
      setCopiedPassword(false);
      
      toast.success(`Password reset successfully for ${parentName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleCopyPassword = () => {
    if (resetPasswordData?.newPassword) {
      navigator.clipboard.writeText(resetPasswordData.newPassword);
      setCopiedPassword(true);
      toast.success('Password copied to clipboard!');
      setTimeout(() => setCopiedPassword(false), 3000);
    }
  };

  const handleCopyLoginDetails = () => {
    if (resetPasswordData) {
      const text = `Login Details for ${resetPasswordData.parentName}\nPhone: ${resetPasswordData.phone}\nPassword: ${resetPasswordData.newPassword}`;
      navigator.clipboard.writeText(text);
      toast.success('Login details copied to clipboard!');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Parent Management</h1>
        <button
          onClick={() => handleAddOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Parent
        </button>
      </div>

      {/* Two Search Boxes Side by Side */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Search className="w-4 h-4 inline mr-2" />
            Search by Parent Name / Phone
          </label>
          <input
            type="text"
            placeholder="Enter parent name or phone..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={parentNameSearch}
            onChange={(e) => setParentNameSearch(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-2" />
            Search by Child Name
          </label>
          <input
            type="text"
            placeholder="Enter child name..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={childNameSearch}
            onChange={(e) => setChildNameSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Results Count */}
      {(parentNameSearch || childNameSearch) && (
        <div className="mb-4 text-sm text-gray-600">
          Found {parents.length} parent(s)
        </div>
      )}

      {/* Parents Grid */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : parents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No parents found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {parents.map((parent) => (
            <div 
              key={parent.id} 
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleOpenModal(parent)}
            >
              {/* Parent Names */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {parent.father_name && parent.mother_name 
                      ? `${parent.father_name} & ${parent.mother_name}`
                      : parent.father_name || parent.mother_name || parent.name}
                  </h3>
                </div>
                
                {/* Contact Info */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Phone className="w-4 h-4" />
                  {parent.phone}
                </div>
                
                {parent.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                    <Mail className="w-4 h-4" />
                    {parent.email}
                  </div>
                )}
              </div>

              {/* Children Info */}
              <div className="mb-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="w-4 h-4" />
                  {parent.children_count || 0} {parent.children_count === 1 ? 'Child' : 'Children'}
                </div>
                
                {parent.children && parent.children.length > 0 && (
                  <div className="space-y-1">
                    {parent.children.map((child, idx) => (
                      <div 
                        key={child.id} 
                        className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChildClick(child.id);
                        }}
                      >
                        {idx === 0 ? '• ' : '• '}{child.name} ({child.class_name})
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenModal(parent)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(parent.id, parent.children_count)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
                <button
                  onClick={(e) => handleResetPassword(e, parent.id, parent.father_name || parent.mother_name || parent.name)}
                  className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors"
                >
                  <Key className="w-4 h-4" />
                  Reset Password
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* add Modal */}
      {addParent && (
        <ParentAddModal onClose={() => setAddParent(!addParent)}/>
      )}
      {/* Edit Modal */}
      {showModal && (
        <ParentEditModal
          isOpen={showModal}
          onClose={handleCloseModal}
          parent={editingParent}
          onChildClick={handleChildClick}
        />
      )}

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-600" />
              Password Reset Successful
            </DialogTitle>
          </DialogHeader>
          
          {resetPasswordData && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  New login credentials for <span className="font-bold">{resetPasswordData.parentName}</span>:
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <div>
                      <p className="text-xs text-gray-500">Phone Number</p>
                      <p className="font-mono font-semibold">{resetPasswordData.phone}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between bg-white rounded p-2">
                    <div>
                      <p className="text-xs text-gray-500">New Password</p>
                      <p className="font-mono font-semibold text-lg">{resetPasswordData.newPassword}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyPassword}
                      className="ml-2"
                    >
                      {copiedPassword ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ <strong>Important:</strong> Save this password now. You won't be able to see it again. 
                  Share it with the parent via WhatsApp, SMS, or in person.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCopyLoginDetails}
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Details
                </Button>
                <Button
                  onClick={() => setShowPasswordDialog(false)}
                  className="flex-1 btn-primary text-white"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentManagement;
